// Drag-to-pan for a horizontally scrollable strip: grab it anywhere with the mouse and pull it
// left or right, instead of hunting for the thin scrollbar or knowing about shift+wheel. The
// header balances are the caller — a single line that routinely overflows the space between the
// logo and the account menu.
//
// Only the mouse is hijacked: touch and pen already pan natively (with momentum), and taking the
// pointer over would replace that with a worse imitation. The cursor is written inline rather than
// via a class because the class would live outside the component's markup, where Svelte's CSS
// pruning cannot see it.

// A sub-pixel difference is measurement noise, not something to offer a grab handle for.
const OVERFLOW_SLACK = 1;

export function dragScroll(node) {
  let startX = 0;
  let startScrollLeft = 0;
  let dragging = false;

  const overflows = () => node.scrollWidth - node.clientWidth > OVERFLOW_SLACK;

  // Shown whenever there is somewhere to drag to; nothing to pan means no false affordance.
  const showGrabCursor = () => {
    node.style.cursor = overflows() ? "grab" : "";
  };

  const onPointerDown = (event) => {
    if (event.pointerType !== "mouse" || event.button !== 0 || !overflows()) return;
    dragging = true;
    startX = event.clientX;
    startScrollLeft = node.scrollLeft;
    node.style.cursor = "grabbing";
    // Dragging across text would otherwise select it, leaving the strip highlighted afterwards.
    node.style.userSelect = "none";
    event.preventDefault();
    // On the window, not the node: the pointer regularly leaves the strip mid-drag (it is only a
    // header line tall) and the drag has to keep following it until the button comes back up.
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", stopDragging);
    window.addEventListener("pointercancel", stopDragging);
  };

  const onPointerMove = (event) => {
    if (!dragging) return;
    node.scrollLeft = startScrollLeft - (event.clientX - startX);
  };

  const stopDragging = () => {
    if (!dragging) return;
    dragging = false;
    node.style.userSelect = "";
    showGrabCursor();
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", stopDragging);
    window.removeEventListener("pointercancel", stopDragging);
  };

  node.addEventListener("pointerdown", onPointerDown);
  showGrabCursor();

  // The strip's own width follows the header (window resizes, the sidebar, the environment chip);
  // its content width follows the balances, which the caller re-runs the action for.
  const observer =
    typeof ResizeObserver === "undefined" ? null : new ResizeObserver(showGrabCursor);
  observer?.observe(node);

  return {
    update: showGrabCursor,
    destroy() {
      stopDragging();
      observer?.disconnect();
      node.removeEventListener("pointerdown", onPointerDown);
    },
  };
}
